import { Button } from "@mui/material";
import Papa from "papaparse";
import React from "react";
import { toast } from "react-toastify";
import { IFeedbackData, IRunData, jsonFields } from "../../Helper";
import pb from "../../pocketBase";

interface ExportCsvButtonProps {
  sessionId?: string;
}

interface ExpandedRun extends IRunData {
  id: string;
  created: string;
  updated: string;
  collectionId: string;
  collectionName: string;
  runComment: string;
  expand?: {
    sessionId?: {
      name: string;
      date: string;
    };
  };
  feedback?: IFeedbackData | null;
  times?: {
    Time: string;
    Cones: number;
    lapTimeSetAt: Date | null;
    timeComment: string;
  }[];
}

const ExportCsvButton: React.FC<ExportCsvButtonProps> = ({ sessionId }) => {
  const fetchAndExportCSV = async () => {
    try {
      const exportSpecificSession = !!sessionId;
      const options: any = {
        expand: 'sessionId',
        sort: 'created'
      };

      options.expand = "sessionId"; // Always expand sessionId

      if (exportSpecificSession) {
        options.filter = `sessionId = "${sessionId}"`;
      }

      // Fetch all runs for the session (or all runs if no sessionId)
      const runs = await pb.collection("Run").getFullList<ExpandedRun>({
      ...options,
      sort: 'created'});

      if (runs.length === 0) {
        toast.error("No runs found for export.");
        return;
      }

      // Fetch feedback and times for each run
      const runsWithDetails = [];
      for (const run of runs) {
        const times = await pb
          .collection("Time")
          .getFullList({ filter: `RunId = "${run.id}"`, sort: "Index" })
          .catch(() => []);
        const feedback = await pb
          .collection("DriverFeedback")
          .getFirstListItem(`run = "${run.id}"`)
          .catch(() => null);
        runsWithDetails.push({ ...run, feedback, times });
      }


      // Flatten to one row per lap time
      const processedRecords = runsWithDetails.flatMap((run) => {
        const sessionName = run.expand?.sessionId?.name || "";
        const sessionDate = run.expand?.sessionId?.date
          ? new Date(run.expand.sessionId.date).toISOString().split("T")[0]
          : "";

        const feedbackData: any = run.feedback || {};

        // Parse JSON fields from run data
        const parsedRun: Record<string, any> = { ...run };
        jsonFields.forEach((field) => {
          const value = parsedRun[field];
          if (typeof value === "string") {
            try {
              const parsed = JSON.parse(value);
              if (Array.isArray(parsed)) {
                parsedRun[field] = parsed.join(" | ");
              }
            } catch {
              parsedRun[field] = "";
            }
          } else if (Array.isArray(parsedRun[field])) {
            parsedRun[field] = parsedRun[field].join(" | ");
          }
        });

        // Serialize software object to readable string
        if (parsedRun.software && typeof parsedRun.software === "object") {
          parsedRun.software = Object.entries(parsedRun.software)
            .map(([device, version]) => `${device}: ${version}`)
            .join(" | ");
        }
        if (parsedRun.software_params && typeof parsedRun.software_params === "object") {
          parsedRun.software_params = Object.entries(parsedRun.software_params)
            .map(([device, params]) =>
              typeof params === "object"
                ? `${device}: ${Object.entries(params as Record<string, any>).map(([k, v]) => `${k}=${v}`).join(", ")}`
                : `${device}: ${params}`
            )
            .join(" | ");
        }

        return run.times?.map((time, index) => ({
          sessionName,
          sessionDate,
          runId: run.id,
          info: parsedRun.info || "",
          driver: parsedRun.driver || "",
          event: parsedRun.event || "",
          runComment: run.runComment || "",
          lapNumber: index + 1,
          timeInSec: parseFloat(time.Time).toFixed(3),
          cones: time.Cones,
          lapTimeSetAt: time.lapTimeSetAt,
          timeComment: time.timeComment || "",
          track_temperature: parsedRun.track_temperature,
          ambient_temperature: parsedRun.ambient_temperature,
          ambient_humidity: parsedRun.ambient_humidity,
          ambient_pressure: parsedRun.ambient_pressure,
          sky_condition: parsedRun.sky_condition,
          frontwing: parsedRun.frontwing,
          rearwing: parsedRun.rearwing,
          undertray: parsedRun.undertray,
          tire_compound: parsedRun.tire_compound,
          tire_pressure: parsedRun.tire_pressure,
          camber_IA: parsedRun.camber_IA,
          tire_temperature: parsedRun.tire_temperature,
          toe: parsedRun.toe,
          springs: parsedRun.springs,
          damper_rebound: parsedRun.damper_rebound,
          damper_compression: parsedRun.damper_compression,
          corner_weight: parsedRun.corner_weight,
          arb_setting: parsedRun.arb_setting,
          software: parsedRun.software,
          software_params: parsedRun.software_params,
          created: parsedRun.created,
          // driver feedback columns
          grip: feedbackData.grip || "",
          limit_Balance: feedbackData.limit_Balance || "",
          control_corner_entry: feedbackData.control_corner_entry || "",
          control_corner_apex: feedbackData.control_corner_apex || "",
          control_corner_exit: feedbackData.control_corner_exit || "",
          stability_entry: feedbackData.stability_entry || "",
          stability_apex: feedbackData.stability_apex || "",
          stability_exit: feedbackData.stability_exit || "",
          braking_stability: feedbackData.braking_stability || "",
          braking_controlability: feedbackData.braking_controlability || "",
          throttle_stability: feedbackData.throttle_stability || "",
          throttle_controlability: feedbackData.throttle_controlability || "",
          comment: feedbackData.comment || ""
        })) || [];
      });

      if (processedRecords.length === 0) {
        toast.error("No lap times found for export.");
        return;
      }

      // Define CSV columns order - all columns explicitly
      const allColumns = [
        "sessionName",
        "sessionDate",
        "runId",
        "lapNumber",
        "timeInSec",
        "cones",
        "lapTimeSetAt",
        "timeComment",
        "info",
        "driver",
        "event",
        "runComment",
        "track_temperature",
        "ambient_temperature",
        "ambient_humidity",
        "ambient_pressure",
        "sky_condition",
        "frontwing",
        "rearwing",
        "undertray",
        "tire_compound",
        "tire_pressure",
        "camber_IA",
        "tire_temperature",
        "toe",
        "springs",
        "damper_rebound",
        "damper_compression",
        "corner_weight",
        "arb_setting",
        "software",
        "software_params",
        "created",
        "grip",
        "limit_Balance",
        "control_corner_entry",
        "control_corner_apex",
        "control_corner_exit",
        "stability_entry",
        "stability_apex",
        "stability_exit",
        "braking_stability",
        "braking_controlability",
        "throttle_stability",
        "throttle_controlability",
        "comment"
      ];

      // Generate CSV string
      const csv = Papa.unparse(processedRecords, {
        delimiter: ",",
        header: true,
        columns: allColumns,
        skipEmptyLines: true,
      });

      // Trigger download
      const blob = new Blob(["\ufeff" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const currentDate = new Date().toISOString().split("T")[0];
      const fileName = exportSpecificSession
        ? `session_${sessionId}_laps_${currentDate}.csv`
        : `all_sessions_laps_until_${currentDate}.csv`;

      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to export data");
      console.error("CSV export failed:", error);
    }
  };

  return (
    <Button variant="contained" color="primary" onClick={fetchAndExportCSV}>
      Export CSV
    </Button>
  );
};

export default ExportCsvButton;
